import { type SearchResult } from "@mirage/service-dbx-api";
import { createChatCompletionObservable } from "@mirage/service-dbx-api/service/grpc/ml_capabilities_apiv2/chat_completions";
import {
  ChatContent,
  ChatMessage,
  SystemMessage,
  UserMessage,
} from "@mirage/service-dbx-api/service/grpc/ml_capabilities_apiv2/gen/common_pb";
import { tagged } from "@mirage/service-logging";
import { getChatCompletionRequest } from "@mirage/shared/stack-item-summary/llmConfigs";
import { atom } from "jotai";
import * as rx from "rxjs";
import { lastValueFrom } from "rxjs";

import type { ConversationSuggestedQuery } from "@mirage/service-conversation/types";
import type { Channel } from "@mirage/shared/search/search-result";
import type { ChatSource } from "../Chat/types";

const logger = tagged("searchPageDataRetrieval");

export interface AIResponse {
  query: string;
  category: string;
  category_explanation?: string;
  ui_explanation?: string;
  confidence: number;
  outputs: OutputType[];
  results: ContentSearchResult[];
  follow_ups?: ConversationSuggestedQuery[];
}

export interface SearchPageDataRetrievalResult {
  title: string;
  description: string | null;
  text?: string;
  fileType: string;
  creator?: string;
  lastUpdated: number | null;
  relevanceScore: number;
  channel: Channel | null | undefined;
  uuid: string;
}

export interface SearchPageDataResponse {
  conversationResponse?: string;
  conversationResponseMarkdown?: string;
  conversationCitations: ChatSource[];
  conversationFollowUps?: ConversationSuggestedQuery[];
  searchResults?: SearchPageDataRetrievalResult[];
  categoryResults?: Record<string, SearchPageDataRetrievalResult[]>;
}

export type OutputType =
  | "followUpQuestions"
  | "conversationResponse"
  | "conversationCitations"
  | "contentList"
  | "imageGrid"
  | "peopleCard"
  | "noResults"
  | "createCanvas";

export type ContentSearchResult =
  | ContentListBlock
  | ConversationResponseBlock
  | ConversationCitationsBlock
  | FollowUpQuestionsBlock
  | ImageGridBlock;

export interface ContentListBlock {
  type: "contentList";
  results: ContentListItem[];
}

export interface ContentListItem {
  title?: string;
  fileType?: string;
  creator?: string;
  relevanceScore?: number;
  lastUpdated?: number;
  uuid: string;
}

export interface ImageGridBlock {
  type: "imageGrid";
  results: ImageGridItem[];
}

export interface ImageGridItem {
  uuid: string;
}

export interface ConversationResponseBlock {
  type: "conversationResponse";
  results: {
    conversationResponse: string;
  };
}

export interface ConversationCitationsBlock {
  type: "conversationCitations";
  results: ConversationCitation[];
}

export interface ConversationCitation {
  url?: string;
  title?: string;
  relevanceScore?: number;
  uuid: string;
}

export interface FollowUpQuestionsBlock {
  type: "followUpQuestions";
  results: FollowUpQuestion[];
}

export interface FollowUpQuestion {
  text: string;
  docIds: string[];
}

export const answerRetrievalCategoryResultsAtom = atom<
  Record<string, SearchResult[]>
>({});

export const callLLM = async ({
  query,
  data,
  prefix = PROMPT_PREFIX,
}: {
  query: string;
  data: SearchPageDataResponse;
  prefix?: string;
}) => {
  const model = "gpt-4o";
  const modelSource = "openai";
  const feature = "dash/compute/dynamic_search_page_result";

  const userMessageContent = `Search Query: ${query}
  Data: ${JSON.stringify(data)}
  `;

  const messages: ChatMessage[] = [
    new ChatMessage({
      messageType: {
        case: "systemMessage",
        value: new SystemMessage({
          content: new ChatContent({
            text: prefix,
          }),
        }),
      },
    }),
    new ChatMessage({
      messageType: {
        case: "userMessage",
        value: new SystemMessage({
          content: new ChatContent({
            text: userMessageContent,
          }),
        }),
      },
    }),
  ];
  const request = getChatCompletionRequest(
    model,
    modelSource,
    feature,
    messages,
  );

  return createChatCompletionObservable(request).pipe(
    rx.scan((fullAnswer, chunk) => fullAnswer + chunk, ""),
    rx.map((fullAnswer) => ({
      answer: { text: fullAnswer, timestamp: Date.now() },
    })),
  );
};

export const getSearchResultsPlacementForChat = async ({
  query,
  prefix = PROMPT_PREFIX_DETERMINE_RESULTS_LOCATION,
}: {
  query: string;
  prefix?: string;
}): Promise<"hidden" | "top" | "bottom"> => {
  const model = "gpt-4o";
  const modelSource = "openai";
  const feature = "dash/compute/search_results_in_chat";

  const messages: ChatMessage[] = [
    new ChatMessage({
      messageType: {
        case: "systemMessage",
        value: new SystemMessage({
          content: new ChatContent({
            text: prefix,
          }),
        }),
      },
    }),
    new ChatMessage({
      messageType: {
        case: "userMessage",
        value: new UserMessage({
          content: new ChatContent({
            text: `Search Query: ${query}`,
          }),
        }),
      },
    }),
  ];
  const request = getChatCompletionRequest(
    model,
    modelSource,
    feature,
    messages,
  );

  const observable = createChatCompletionObservable(request).pipe(
    rx.scan((fullAnswer, chunk) => fullAnswer + chunk, ""),
    rx.last(),
    rx.map((fullAnswer) => {
      try {
        const parsed = JSON.parse(fullAnswer);
        return parsed.placement;
      } catch (err) {
        logger.error("Error parsing JSON from LLM");
        return "hidden";
      }
    }),
  );

  return lastValueFrom(observable).catch((err) => {
    if (err.name === "EmptyError") {
      throw new Error("No result received from LLM");
    }
    throw err;
  });
};

export const PROMPT_PREFIX_DETERMINE_RESULTS_LOCATION = `
You are a classifier that decides where (or whether) to show the "Top results" block
in our chat UI. Read a single user query provided in the format:


Search Query: <query>

Reply with a *single* JSON object ONLY:
• Do NOT wrap it in triple back-ticks or any other fencing.  
• Do NOT add json, comments, or extra text – only the JSON itself.
{
  "placement": "top" | "bottom" | "hidden",
  "confidence": 0-1      // 1 = absolutely certain
}

Definitions
-----------
• "Content-search" intent → user wants specific files or lists of files. Queries are usually short noun phrases ("planning docs", "healthcare plan") or include verbs like "find", "show", "open".
• Even unfamiliar phrases or titles (e.g., "search in chat", "start page jamming") that sound like file or doc names should count as content-search.
    ⇒ placement = "top"   (show Top results above the chat answer)
• "Chat/Q&A" intent → user expects a synthesized answer, explanation, or advice. Often starts with wh-words or is phrased as a question ("What is…", "How do we…").
    ⇒ placement = "bottom" (show Top results below answer)
• If the query is long, clearly a question, or if showing raw results would add no value.
    ⇒ placement = "hidden"


Edge rules
----------
1. If confidence < 0.55, prefer "bottom" to stay unobtrusive.
2. ALWAYS output valid JSON. No other text.
3. If the query contains temporal words (tomorrow, next week, Q3, 2025) and no file keywords, treat as Q&A ⇒ "bottom".
4. Rule of thumb: “If I can prefix the query with ‘What/How/When’ and it still makes sense, it’s probably Q&A and should be "bottom"


Few-shot examples
-----------------
User: planning docs  
Assistant: {"placement":"top","confidence":0.92}

User: healthcare plan  
Assistant: {"placement":"top","confidence":0.88}

User: verizon bill  
Assistant: {"placement":"top","confidence":0.90}

User: what is the main goal for H2 planning?  
Assistant: {"placement":"bottom","confidence":0.85}

User: what is our company's PTO policy  
Assistant: {"placement":"bottom","confidence":0.87}

User: roadmap  
Assistant: {"placement":"top","confidence":0.60}

User: plans for h2
Assistant: {"placement":"bottom","confidence":0.70}

User: weather tomorrow
Assistant: {"placement":"bottom","confidence":0.78}

User: quarterly plan
Assistant: {"placement":"bottom","confidence":0.78}

User: search in chat
Assistant: {"placement":"top","confidence":0.85}

User: start page jamming
Assistant: {"placement":"top","confidence":0.82}

User: Q2 growth charts
Assistant: {"placement":"top","confidence":0.87}

User: project thor updates
Assistant: {"placement":"top","confidence":0.83}

User: how did project thor perform in Q2?
Assistant: {"placement":"bottom","confidence":0.82}

User: moonshot summaries
Assistant: {"placement":"top","confidence":0.81}

<END OF FEW-SHOTS>

Now classify the next query.
`;

export const PROMPT_PREFIX = `
You are an employee of a Company searching on Dropbox Dash about your Company. Given a search Query on dash and associated Search Result Items, returned a category of search from the Category Types list below. If a judgment would be an obvious content safety violation, then assign the category "Generic or Unknown.” If a judgement is unknown from the query, identify the Search Results Item with relevanceScore of 0.5 or higher. From the search results provided, Use the content types with the highest relevance score items to determine a category. If a judgement is still unclear, then assign a Generic or Unknown category.
Category Types:

- Generic or Unknown
- Content Search
- Navigation
- Media Search
- Multi-Turn Question and Answer
- Quick Answer
- People Search

Assume that Queries are motivated to find recent, reliable, and authoritative information about your Company. Think about, given the user Query, what is the likely user intent or information need, and how much information might be needed to satisfy that intent, and which of the keywords and phrases in the query are required to match this intent. If a Query is a broad category or general topic of common words, assign a Generic type. When the query seems to be a question, highly prioritize either the Multi-Turn Conversation Category or the Quick Answer Category. 
When the query is only what seems to be a person’s name, highly prioritize People Search. Otherwise, if the query partially contains a person’s name alongside other words, determine whether the intent is to find information about that person or find documents from that person. If the additional keywords are regarding a person’s role, team, birthday, position in the company, prioritize People Search. If the additional keywords are descriptors of a topic, prioritize Document Search.
When the query contains keywords describing a multimedia content type, such as images, videos, audio, movies, photos, assign the Media category. Think about whether the query intent is to gather many media items matching a descriptor, and whether the desire is for many images or a single specific one.
When the query contains keywords that seem highly specific to a company context, project or topic, or seem to match what is likely the name of an internal tool, content, document where the user intent is to access a specific item they have in mind, prioritize the Navigation category. When the query contains a specific content type, such as pdfs, documents or an application name, like Jira or Dropbox, assign the Content Search category. When the query suggests that there may be multiple matching results, assign the Content Search category. If the query contains words or phrases such as find, show, gather, ‘all of’, assign the Content Search category.
If the query contains words or phrases such as create, draft, write, make, assign the Create category.
Use the category type descriptions and the query to determine the right experience output. Think about what the user is trying to achieve, and construct the output based on the output blocks provided in the Schema below.
Use the provided Search Page Result Items and determine which results to include. Pass the results back in results, and include the conversationResponse provided as well. Make sure to include the category results from messages and images, especailly if they have a high relevance rating of above 0.5. Only include results that have a relevance rating of 0.5 or above. If something has a relevance rating of 0.4 or lower, exclude them from the main results list. Instead, include a separate section collating all those results and title it “Other Results”
Given a Query, respond with a single Category Type. Defend your rating in the explanation field using specific examples and why another category is not a better rating selection in at most three sentences. Based on the Category Type, respond with the output blocks to be included to show the user. Select from Output Block list below and provide the subset of output blocks and the correct order you determine based on the category type and what you determine the correct user experience to be to meet the user’s desired intent. Return the narrowed down list of Search Result Items from the input, and allott them to the selected output blocks.
Output Blocks:
-Follow-up Questions: “followUpQuestions”
-Conversation Response: “conversationResponse”
-Conversation Citations: “conversationCitations”
-Sources: “sources”
-Content List: “contentList”
-Image Grid: “imageGrid”
-People Card: “peopleCard”
-Error Message: “errorMessage”
-No Results: “noResults”
-Canvas: “createCanvas”,
Determine what blocks are necessary based on the intent and provide rationale. Always include at least one of the Conversation Response blocks. If category is People Search, prioritize the People Card block, Also include a Document List if the name in the query matches the modifier, owner or shared by for any of the Result Items. If the category is Media, prioritize the Image Grid block. . If the category is Content Search, prioritize the Content List block. Only return the most relevant results, including messages and emails. If the category is Create, prioritize the Canvas block. Also include a ConversationResponse, Sources and Follow-up Questions. If the category is generic, use the most relevant results to determine an improved category. If no improved category is found, return Document List with the results with a rating of 3 or higher. If no results remain, then return the No Results block.
Examples:
Search Query: “code yellow 2025”
Category: Content Search
Search Query: “nelson birthday”

    Category: People Search
    Output Blocks: Conversation Response, People Card,

Search Query: “nelson williams”

    Category: People Search
    Output Blocks: People Card, Answer

Search Query: “how to file a code yellow”

    Category: Multi-Turn Answer
    Output Blocks: Answer, Document List
    -Search Query: “avery davis docs”
    Category: Content Search
    Output Blocks: Document List, Conversation Response, People Card

Based on these output blocks, transform the Search Results given as an input to the correct output. Include all data that was passed in for each item. Respond in JSON format, and do not wrap in JSON markers.

    {
      query: string,
      category: string,
      category_explanation: string,
      ui_explanation: string,
      confidence: number;
      outputs: [ string ],
      results: [ { 
    type: output,
    results: {result output}
    ]

}
Provide output blocks in the order that you determined they should be shown in the UI explanation. provide a list of output blocks in the outputs field. Then, pass Use the conversationResponse, conversationCitations and conversationMarkdown that is passed in the input - do not create your own. Combine search results, messages, emails and images into content_list results. When returning conversationCitations, messages, images, emails and search results, order the content_list results returned by relevance score.

For this question please ignore all results fields except uuid and do not provide a category_explanation or ui_explanation. Only include contentList and imageGrid types in the results array. Only return the JSON object with the fields specified above and it must be parsable using JSON.parse.`;

export const PROMPT_PREFIX_DYNAMIC = `
You are an employee of a Company searching on Dropbox Dash about your Company. If a judgement is unknown from the query, identify the Search Results Item with relevanceScore of 0.5 or higher.
Category Types:

- Generic or Unknown
- Content Search
- Navigation
- Media Search
- Multi-Turn Question and Answer
- Quick Answer
- People Search

Assume that Queries are motivated to find recent, reliable, and authoritative information about your Company. Think about, given the user Query, what is the likely user intent or information need, and how much information might be needed to satisfy that intent, and which of the keywords and phrases in the query are required to match this intent. If a Query is a broad category or general topic of common words, assign a Generic type. When the query seems to be a question, highly prioritize either the Multi-Turn Conversation Category or the Quick Answer Category. 
When the query is only what seems to be a person’s name, highly prioritize People Search. Otherwise, if the query partially contains a person’s name alongside other words, determine whether the intent is to find information about that person or find documents from that person. If the additional keywords are regarding a person’s role, team, birthday, position in the company, prioritize People Search. If the additional keywords are descriptors of a topic, prioritize Document Search.
When the query contains keywords describing a multimedia content type, such as images, videos, audio, movies, photos, assign the Media category. Think about whether the query intent is to gather many media items matching a descriptor, and whether the desire is for many images or a single specific one.
When the query contains keywords that seem highly specific to a company context, project or topic, or seem to match what is likely the name of an internal tool, content, document where the user intent is to access a specific item they have in mind, prioritize the Navigation category. When the query contains a specific content type, such as pdfs, documents or an application name, like Jira or Dropbox, assign the Content Search category. When the query suggests that there may be multiple matching results, assign the Content Search category. If the query contains words or phrases such as find, show, gather, ‘all of’, assign the Content Search category.
If the query contains words or phrases such as create, draft, write, make, assign the Create category.
Use the category type descriptions and the query to determine the right experience output. Think about what the user is trying to achieve, and construct the output based on the output blocks provided in the Schema below.
Use the provided Search Page Result Items and determine which results to include. Pass the results back in results, and include the conversationResponse provided as well. Make sure to include the category results from messages and images, especailly if they have a high relevance rating of above 0.5. Only include results that have a relevance rating of 0.5 or above. If something has a relevance rating of 0.4 or lower, exclude them from the main results list. Instead, include a separate section collating all those results and title it “Other Results”
Given a Query, respond with a single Category Type. Defend your rating in the explanation field using specific examples and why another category is not a better rating selection in at most three sentences. Based on the Category Type, respond with the output blocks to be included to show the user. Select from Output Block list below and provide the subset of output blocks and the correct order you determine based on the category type and what you determine the correct user experience to be to meet the user’s desired intent. Return the narrowed down list of Search Result Items from the input, and allott them to the selected output blocks.
Output Blocks:
-Follow-up Questions: “followUpQuestions”
-Conversation Response: “conversationResponse”
-Conversation Citations: “conversationCitations”
-Sources: “sources”
-Content List: “contentList”
-Image Grid: “imageGrid”
-People Card: “peopleCard”
-Error Message: “errorMessage”
-No Results: “noResults”
-Canvas: “createCanvas”,
Determine what display is necessary based on the intent and provide rationale. Always include at least one of the Conversation Response blocks. If category is People Search, prioritize the People Card block, Also include a Document List if the name in the query matches the modifier, owner or shared by for any of the Result Items. If the category is Media, prioritize the Image Grid block. . If the category is Content Search, prioritize the Content List block. Only return the most relevant results, including messages and emails. If the category is Create, prioritize the Canvas block. Also include a ConversationResponse, Sources and Follow-up Questions. If the category is generic, use the most relevant results to determine an improved category. If no improved category is found, return Document List with the results with a rating of 3 or higher. If no results remain, then return the No Results block.
Examples:
Search Query: “code yellow 2025”
Category: Content Search
Search Query: “nelson birthday”

    Output:
        <answer response>
        [image:<image_id1>,<image_id2>,...]

Search Query: “nelson williams”

    Output:
        [person:<email>]
        <answer response>

Search Query: “how to file a code yellow”

    Output:
        [results:<id1>,<id2>,...]
        <answer response>

Based on these output blocks, transform the Search Results given as an input to the correct output. For output blocks other than the conversation response or answer use the following format:

People Card: "[person:<email>]"
Result List: "[results:<id1>,<id2>,...,<idN>]"
Media List: "[media:<id1>,<id2>,<idN>]"

For IDs use the id_no field that we're passing in to identifiy a result

For all other types, such as the conversation response or follow up questions, just use the actual text and citations from the answer response.

The output should be human readable except for citations (from the answer response) and the custom markdown indicated above. For this query, skip the category and explanation field and only give me the output and separate the "blocks" by newlines`;
